import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { CheckIcon } from '@/components/Icons'
import type { BacklogMember } from '@ai-meeting/shared'

/** Narrowest the open panel may be — same floor as {@link TenantUserSelect}. */
const PANEL_MIN_WIDTH_PX = 300
const PANEL_MAX_HEIGHT_PX = 320
const PANEL_GAP_PX = 4

export interface BacklogMemberSelectProps {
  /** Selected Backlog member id, or null when nothing is chosen. */
  value: number | null
  onChange: (member: BacklogMember | null) => void
  members: BacklogMember[]
  placeholderLabel: string
  searchPlaceholder: string
  noResultsLabel: string
  loadingLabel: string
  errorMessage: string | null
  retryLabel: string
  onRetry?: () => void
  loading?: boolean
  disabled?: boolean
  readOnly?: boolean
  /** Display name shown in read-only / when the id is not in `members`. */
  displayName?: string
  dataField?: string
  ariaLabel?: string
}

/**
 * Assignee picker fed by a Backlog project roster.
 *
 * Same face as {@link TenantUserSelect} (fixed panel, search) so the
 * Next-actions Assignee column does not change shape — only the source of the
 * rows. Each row is the name alone: a second line (a Backlog `user_id`, or a
 * mail address some members lack) makes that row taller than the rest
 * (BRAI-721). The chosen row keeps the accent and a check, separate from the
 * hover highlight. Search still matches the handle and the address. Selecting
 * writes a numeric id; clearing is the reviewer's choice of "nobody".
 */
export default function BacklogMemberSelect({
  value,
  onChange,
  members,
  placeholderLabel,
  searchPlaceholder,
  noResultsLabel,
  loadingLabel,
  errorMessage,
  retryLabel,
  onRetry,
  loading = false,
  disabled = false,
  readOnly = false,
  displayName,
  dataField,
  ariaLabel,
}: BacklogMemberSelectProps) {
  const listboxId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const activeRowRef = useRef<HTMLButtonElement>(null)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({})

  const selected = value == null ? undefined : members.find((member) => member.id === value)
  const closedLabel = selected?.name ?? displayName ?? ''

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return members
    return members.filter((member) => {
      const haystack = `${member.name} ${member.user_id ?? ''} ${member.mail_address ?? ''}`.toLowerCase()
      return haystack.includes(needle)
    })
  }, [members, search])

  useEffect(() => {
    if (!open) return undefined
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  useEffect(() => {
    if (open) searchRef.current?.focus()
  }, [open])

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  useLayoutEffect(() => {
    if (!open) return undefined

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return

      const width = Math.max(rect.width, PANEL_MIN_WIDTH_PX)
      const spaceBelow = window.innerHeight - rect.bottom
      const openUpwards = spaceBelow < PANEL_MAX_HEIGHT_PX && rect.top > spaceBelow

      setPanelStyle({
        position: 'fixed',
        width,
        left: Math.max(PANEL_GAP_PX, Math.min(rect.left, window.innerWidth - width - PANEL_GAP_PX)),
        ...(openUpwards
          ? { bottom: window.innerHeight - rect.top + PANEL_GAP_PX }
          : { top: rect.bottom + PANEL_GAP_PX }),
        zIndex: 100,
      })
    }

    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  const openList = () => {
    setSearch('')
    setActiveIndex(0)
    setOpen(true)
  }

  const choose = (member: BacklogMember) => {
    setOpen(false)
    onChange(member)
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((current) => Math.min(current + 1, Math.max(matches.length - 1, 0)))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((current) => Math.max(current - 1, 0))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const member = matches[activeIndex]
      if (member) choose(member)
    }
  }

  if (readOnly) {
    return (
      <span title={closedLabel || undefined} className="block w-full px-2 py-1 text-left text-sm">
        {closedLabel || <span className="text-[var(--text-tertiary)]">{placeholderLabel}</span>}
      </span>
    )
  }

  if (errorMessage) {
    return (
      <div className="space-y-1">
        <p role="alert" className="text-xs text-red-600">
          {errorMessage}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-semibold text-[var(--accent)]"
          >
            {retryLabel}
          </button>
        )}
      </div>
    )
  }

  const triggerDisabled = disabled || loading

  return (
    <div ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        data-minutes-field={dataField}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        disabled={triggerDisabled}
        onClick={() => (open ? setOpen(false) : openList())}
        className="input-field flex items-center justify-between gap-2 text-left disabled:opacity-50"
      >
        {loading ? (
          <span className="truncate text-[var(--text-tertiary)]">{loadingLabel}</span>
        ) : closedLabel ? (
          <span className="truncate text-[var(--text-primary)]">{closedLabel}</span>
        ) : (
          <span className="truncate text-[var(--text-tertiary)]">{placeholderLabel}</span>
        )}
        <span aria-hidden="true" className="shrink-0 text-[var(--text-tertiary)]">
          ▾
        </span>
      </button>

      {open && !loading && (
        <div
          style={panelStyle}
          className="overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-lg"
        >
          <div className="border-b border-[var(--border-color)] p-2">
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-expanded
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setActiveIndex(0)
              }}
              onKeyDown={handleSearchKeyDown}
              className="w-full rounded-lg bg-[var(--bg-tertiary)] px-2.5 py-1.5 text-sm text-[var(--text-primary)] outline-none"
            />
          </div>

          <ul id={listboxId} role="listbox" className="max-h-64 overflow-y-auto py-1">
            {value != null && !search.trim() && (
              <li role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    setOpen(false)
                    onChange(null)
                  }}
                  className="flex w-full px-3 py-2 text-left text-sm text-[var(--text-tertiary)]"
                >
                  {placeholderLabel}
                </button>
              </li>
            )}

            {matches.length === 0 && (
              <li className="px-3 py-2 text-xs text-[var(--text-tertiary)]">{noResultsLabel}</li>
            )}

            {matches.map((member, index) => {
              const chosen = member.id === value
              return (
                <li key={member.id} role="presentation">
                  <button
                    ref={index === activeIndex ? activeRowRef : undefined}
                    type="button"
                    role="option"
                    aria-selected={chosen}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(member)}
                    className={[
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                      index === activeIndex ? 'bg-[var(--bg-tertiary)]' : '',
                      chosen ? 'font-semibold text-[var(--accent)]' : '',
                    ].join(' ')}
                  >
                    <span className="min-w-0 flex-1 truncate">{member.name}</span>
                    {chosen ? <CheckIcon className="h-3.5 w-3.5 shrink-0" /> : null}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
